// Added-to-cart sheet offer chain (Blanc Space custom) — markup in
// snippets/added-sheet-offers.liquid, rendered inside sections/variant-added.liquid.
//
// - One surface: everything happens inside the sheet that already opened on Add to
//   cart. Adds and the Impact Pro swap here are dispatched as BLOCKED variant:add
//   events (so neither the sheet nor the cart drawer reopens or stacks) plus a
//   cart:change (drawer, badge, BOGOF and smart offer refresh), and the sheet
//   re-renders itself from the same request's bundled `variant-added` section.
// - Two parts (data-part): the "Upgrade to Impact Pro?" strip under the item card,
//   and the "Make it match" tiles after the BOGOF card. Both show at once; ignoring
//   the strip is the decline. Upgrading re-renders the sheet for the Pro line, which
//   brings the matching grip into the tiles.
// - On for everyone since 30 Oct 2026 (the 50/50 `added_chain_arm` test ended);
//   ?bs_chain=control forces the old sheet in one tab for testing.

(() => {
	if (window.__addedSheetOffersInitialized) return;
	window.__addedSheetOffersInitialized = true;

	const ARM_KEY = 'blancspace.addedChainArm';
	const REMOVED_KEY = 'blancspace.upsellRemoved'; // shared with custom.js / upsell-protector.js

	const readSet = (store, key) => {
		try { return new Set((JSON.parse(store.getItem(key) || '[]') || []).map(String)); } catch (error) { return new Set(); }
	};
	const writeSet = (store, key, set) => {
		try { store.setItem(key, JSON.stringify([...set])); } catch (error) { /* storage unavailable */ }
	};
	const track = (name, props) => {
		try { window.bsTrack?.(name, props); } catch (error) { /* analytics must never break shopping */ }
	};

	// The 50/50 test ended 30 Oct 2026 (Scott): no harm to checkout starts or cases per
	// order, ~14% more accessories per order. Everyone gets the chain now, including
	// browsers stored as 'control'. ?bs_chain=control still forces the old sheet for
	// the rest of that tab, for testing only.
	const arm = (() => {
		try {
			const forced = new URL(window.location.href).searchParams.get('bs_chain');
			if (forced === 'chain' || forced === 'control') sessionStorage.setItem(ARM_KEY, forced);
			localStorage.removeItem(ARM_KEY); // retire the stored 50/50 arm
			return sessionStorage.getItem(ARM_KEY) === 'control' ? 'control' : 'chain';
		} catch (error) {
			return 'chain';
		}
	})();
	try { window.posthog?.register?.({ added_chain_arm: arm }); } catch (error) { /* posthog not loaded */ }

	const root = () => window.Shopify?.routes?.root || '/';
	// Customily's store-level requestSkipping hook ignores adds carrying this flag; without it
	// an add made on a personalised PDP is treated as a new personalisation. Shopify ignores it.
	const CUSTOMILY_SKIP = '?sliderCartOpen=false';

	// Items the sheet opened with (case + PDP add-ons), so an in-sheet add can rebuild
	// the carousel. Only unblocked adds open the sheet, so only those reset it.
	let sheetItems = [];
	document.addEventListener('variant:add', (event) => {
		if (!event.detail?.blockCartDrawerOpening && Array.isArray(event.detail?.items)) {
			sheetItems = event.detail.items.slice();
		}
	});

	const bundledSections = () => {
		const sections = ['variant-added'];
		document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } }));
		return [...new Set(sections)];
	};

	class AddedSheetOffers extends HTMLElement {
		connectedCallback() {
			if (this._connected) return;
			this._connected = true;
			if (arm !== 'chain') return;

			if (this.dataset.part === 'match') {
				// Anything the shopper unticked or removed this session is not offered again.
				const removed = readSet(sessionStorage, REMOVED_KEY);
				this.querySelectorAll('[data-offer-tile]').forEach((tile) => {
					if (removed.has(String(tile.dataset.productId))) tile.remove();
				});
				if (!this.querySelector('[data-offer-tile]')) return;
			} else if (!this.querySelector('[data-offer-upgrade]')) {
				return;
			}

			this.hidden = false;
			track('added_sheet_offers_shown', {
				part: this.dataset.part,
				tiles: [...this.querySelectorAll('[data-offer-tile]')].map((tile) => tile.dataset.handle),
			});

			this.addEventListener('click', (event) => {
				const target = event.target instanceof Element ? event.target : null;
				const upgrade = target?.closest('[data-offer-upgrade]');
				if (upgrade) this._upgrade(upgrade);
				const add = target?.closest('[data-offer-add]');
				if (add) this._add(add);
			});
		}

		_busy(button, busy, label) {
			button.disabled = busy;
			button.setAttribute('aria-busy', String(busy));
			const text = button.querySelector('[data-offer-add-label]') || button;
			if (label) text.textContent = label;
		}

		async _add(button) {
			if (this._working) return;
			this._working = true;
			const tile = button.closest('[data-offer-tile]');
			const source = `added_sheet_${tile?.dataset.offerSource || 'accessory'}`;
			this._busy(button, true, 'Adding…');
			try {
				const sections = bundledSections();
				const response = await fetch(`${root()}cart/add.js${CUSTOMILY_SKIP}`, {
					method: 'POST',
					headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
					body: JSON.stringify({
						items: [{ id: parseInt(button.dataset.offerAdd, 10), quantity: 1 }],
						sections: sections.join(','),
						sections_url: `${root()}variants/${this.dataset.anchorVariant}`,
					}),
				});
				const json = await response.json();
				if (!response.ok) throw new Error(json.description || 'add failed');
				const added = (json.items || []).map((item) => ({ ...item, bs_source: source }));
				await this._afterCartUpdate(json.sections, added, (items) => [...items, ...added]);
				// Still attached = the sheet HTML was missing from the response (throttled); the
				// cart has the item, so say so instead of leaving the button stuck on 'Adding…'.
				if (this.isConnected) { this._busy(button, true, 'Added'); this._working = false; }
			} catch (error) {
				this._busy(button, false, 'Try again');
				this._working = false;
			}
		}

		async _upgrade(button) {
			if (this._working) return;
			this._working = true;
			this._busy(button, true, 'Upgrading…');
			const proId = parseInt(button.dataset.offerUpgrade, 10);
			const lineKey = this.dataset.lineKey;
			try {
				const cart = await (await fetch(`${root()}cart.js`, { headers: { Accept: 'application/json' } })).json();
				const line = (cart.items || []).find((item) => item.key === lineKey);
				if (!line) throw new Error('line gone');
				// Add the Pro first, then remove the old line: the cart is never briefly
				// empty (custom.js empties a protection-only cart).
				// Every line property is copied, Customily's private _customily-* keys included,
				// so a personalised design moves to the Pro line unchanged.
				const addResponse = await fetch(`${root()}cart/add.js${CUSTOMILY_SKIP}`, {
					method: 'POST',
					headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
					body: JSON.stringify({ items: [{ id: proId, quantity: line.quantity, properties: line.properties || {} }] }),
				});
				const addJson = await addResponse.json();
				if (!addResponse.ok) throw new Error(addJson.description || 'upgrade failed');
				// The add can re-key the old line (a second case starts the BOGOF discount
				// and Shopify re-keys discounted lines), so look it up again by variant.
				const after = await (await fetch(`${root()}cart.js`, { headers: { Accept: 'application/json' } })).json();
				// Two personalised cases of one variant are two lines: match the properties too.
				const sameProps = (item) => JSON.stringify(item.properties || {}) === JSON.stringify(line.properties || {});
				const oldLine = (after.items || []).find((item) => item.key === lineKey)
					|| (after.items || []).find((item) => item.variant_id === line.variant_id && item.id !== proId && sameProps(item))
					|| (after.items || []).find((item) => item.variant_id === line.variant_id);
				if (!oldLine) throw new Error('old line gone');
				const sections = bundledSections();
				const changeResponse = await fetch(`${root()}cart/change.js`, {
					method: 'POST',
					headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
					body: JSON.stringify({ id: oldLine.key, quantity: 0, sections: sections.join(','), sections_url: `${root()}variants/${proId}` }),
				});
				const changeJson = await changeResponse.json();
				if (!changeResponse.ok) throw new Error(changeJson.description || 'swap failed');
				const proItems = (addJson.items || []).map((item) => ({ ...item, bs_source: 'added_sheet_upgrade' }));
				track('added_sheet_upgraded', {
					from_case_type: line.variant_options?.[1] || null,
					phone_model: line.variant_options?.[0] || null,
					price_diff: (parseInt(button.dataset.offerDiff, 10) || 0) / 100,
				});
				await this._afterCartUpdate(changeJson.sections, proItems, (items) => {
					const swapped = items.map((item) => (item.variant_id === line.variant_id ? proItems[0] : item));
					return swapped.includes(proItems[0]) ? swapped : [proItems[0], ...items];
				});
				if (this.isConnected) { this._busy(button, true, 'Upgraded'); this._working = false; }
			} catch (error) {
				this._busy(button, false, 'Try again');
				this._working = false;
			}
		}

		async _afterCartUpdate(sections, addedItems, nextItems) {
			const cart = await (await fetch(`${root()}cart.js`, { headers: { Accept: 'application/json' } })).json();
			cart.sections = sections || {};

			// Re-render the sheet FIRST: the express-wallet re-seat in custom.js runs on the
			// events below and must find the new sheet's slot, not the detached old one.
			const drawer = this.closest('cart-notification-drawer');
			const html = cart.sections['variant-added'];
			sheetItems = nextItems(sheetItems);
			// DOMParser, never innerHTML on a live node: fetched custom elements must not
			// upgrade until they are adopted (see AGENTS.md Back-cache restore profile).
			const fresh = html ? new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section') : null;
			if (drawer && fresh) {
				drawer.replaceChildren(...fresh.children);
				window.bsHydrateVariantAddedItems?.(drawer, sheetItems);
			}

			// Blocked: analytics, tombstone forgiveness and the protector list hear the add,
			// but neither the sheet nor the cart drawer reopens.
			document.documentElement.dispatchEvent(new CustomEvent('variant:add', {
				bubbles: true,
				detail: { items: addedItems, cart, blockCartDrawerOpening: true },
			}));
			document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
				bubbles: true,
				detail: { baseEvent: 'variant:add', cart },
			}));
		}
	}

	if (!customElements.get('added-sheet-offers')) {
		customElements.define('added-sheet-offers', AddedSheetOffers);
	}
})();
